import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown, Sparkles, ClipboardCheck, Target, Zap, Bot, BarChart3, TrendingUp, Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

/**
 * SIMPLIFIED NAV (audit remediation + Lead Gen pivot):
 *   6 informational items + 1 primary CTA.
 *   Groups: Services · Lead Generation (mega) · Industries · Results · Resources · Pricing.
 *   Primary CTA: "Get Free Website Audit" (standardised everywhere).
 *
 * The Lead Generation entry is a wide mega-menu grouped by the acquisition
 * lifecycle (Attract · Convert · Automate · Measure · Grow) + two primary
 * entry points (Audit, Growth Advisor). It mirrors `LEADGEN_PAGES` so new
 * services added there automatically surface here if we extend the config
 * below.
 */

// Attract / Convert / Automate / Measure / Grow — pulled from the roadmap.
const LEADGEN_MEGA = {
  key: "lead-generation",
  label: "Lead Generation",
  isMega: true,
  primary: [
    { label: "Lead Generation Audit", to: "/lead-generation/audit", desc: "Score your acquisition engine", Icon: ClipboardCheck },
    { label: "AI Growth Advisor",     to: "/lead-generation#advisor",  desc: "Get a recommended plan",       Icon: Sparkles },
  ],
  groups: [
    { key: "attract", label: "Attract", tagline: "Get found & get traffic", Icon: Target, links: [
      { label: "SEO & Search Visibility",          to: "/lead-generation/seo" },
      { label: "AI Search Visibility (AEO / GEO)", to: "/lead-generation/ai-search" },
      { label: "Social Media Marketing",           to: "/lead-generation/social-media" },
      { label: "Paid Advertising (SEM)",           to: "/lead-generation/paid-advertising" },
      { label: "Branding & Awareness",             to: "/lead-generation/branding" },
    ]},
    { key: "convert", label: "Convert", tagline: "Turn visitors into leads", Icon: Zap, links: [
      { label: "Conversion Rate Optimization", to: "/lead-generation/conversion-optimization" },
      { label: "AI Lead Capture",              to: "/lead-generation/lead-capture" },
      { label: "AI Lead Qualification",        to: "/lead-generation/lead-qualification" },
      { label: "Instant Lead Response",        to: "/lead-generation/instant-response" },
      { label: "Appointment Generation",       to: "/lead-generation/appointments" },
    ]},
    { key: "automate", label: "Automate", tagline: "Respond, nurture & manage", Icon: Bot, links: [
      { label: "CRM & Pipeline Automation",     to: "/lead-generation/crm" },
      { label: "Lead Nurturing & Automation",   to: "/lead-generation/nurturing" },
      { label: "AI Assistants",                 to: "/ai/ai-assistants" },
    ]},
    { key: "measure", label: "Measure", tagline: "See what drives revenue", Icon: BarChart3, links: [
      { label: "Lead Attribution & ROI",   to: "/lead-generation/analytics" },
      { label: "Customer Intelligence",    to: "/lead-generation/customer-intelligence" },
      { label: "Reviews & Reputation",     to: "/lead-generation/reputation" },
    ]},
    { key: "grow", label: "Grow", tagline: "Scale & retain customers", Icon: TrendingUp, links: [
      { label: "Outbound B2B Lead Gen",    to: "/lead-generation/outbound" },
      { label: "Local Business Lead Gen",  to: "/lead-generation/local-business" },
      { label: "Lead Gen as a Service",    to: "/lead-generation/laas" },
      { label: "Industry Lead Gen",        to: "/lead-generation/industries" },
    ]},
  ],
  tools: [
    { label: "AI Revenue Center", to: "/ai-revenue-center",  desc: "Scout · Ava · Relay" },
    { label: "Growth Operator",   to: "/growth-operator",    desc: "Managed marketing + approvals" },
    { label: "Visual Studio",     to: "/visual-studio",      desc: "URL → campaign → SocialPulse" },
    { label: "Tools Catalog",     to: "/tools",              desc: "Every WebsiteTech capability" },
  ],
};

const NAV = [
  {
    key: "services",
    label: "Services",
    links: [
      { label: "Website Design", to: "/build/new-website" },
      { label: "Website Modernization", to: "/improve/website-audit" },
      { label: "SEO & Visibility", to: "/improve/seo" },
      { label: "AI & Automation", to: "/ai/ai-assistants" },
      { label: "Analytics & Conversion", to: "/analytics/website-analytics" },
    ],
    footer: { label: "See all services →", to: "/tools" },
  },
  LEADGEN_MEGA,
  {
    key: "industries",
    label: "Industries",
    links: [
      { label: "Restaurants", to: "/solutions/restaurants" },
      { label: "Healthcare", to: "/solutions/healthcare" },
      { label: "Hotels & Travel", to: "/solutions/travel" },
      { label: "Professional Services", to: "/solutions/professional" },
      { label: "Ecommerce", to: "/solutions/ai-commerce-intelligence" },
    ],
    footer: { label: "View all industries →", to: "/solutions" },
  },
  {
    key: "results",
    label: "Results",
    links: [
      { label: "Case Studies", to: "/case-studies" },
      { label: "Portfolio", to: "/clients" },
      { label: "Before & After", to: "/gallery" },
      { label: "Methodology", to: "/si/methodology" },
      { label: "Client Stories", to: "/proof" },
    ],
  },
  {
    key: "resources",
    label: "Resources",
    links: [
      { label: "SEO Analyzer", to: "/seo-analyzer", desc: "Deterministic page audit" },
      { label: "Guides", to: "/resources/guides" },
      { label: "What's New", to: "/whats-new" },
      { label: "Feature Gallery", to: "/features/gallery" },
      { label: "Website Planner", to: "/planner" },
      { label: "AI Business Center", to: "/business-center" },
    ],
  },
  {
    key: "pricing",
    label: "Pricing",
    links: [
      { label: "Lead Generation Bundles", to: "/lead-generation/bundles", desc: "Start · Grow · Scale · Performance" },
      { label: "Packages", to: "/packages" },
      { label: "Project Starting Prices", to: "/pricing" },
      { label: "Managed Plans", to: "/growth-operator" },
      { label: "Tool Pricing", to: "/tools" },
    ],
  },
];

const PRIMARY_CTA_LABEL = "Get Free Website Audit";
const PRIMARY_CTA_SHORT = "Free Audit";

export function BrandLogo({ size = 44 }) {
  return (
    <div className="flex items-center gap-3" data-testid="brand-logo">
      <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
        <path d="M24 2L43.05 13V35L24 46L4.95 35V13L24 2Z" fill="#2563EB" />
        <path d="M24 2L43.05 13V35L24 46L4.95 35V13L24 2Z" stroke="#1D4ED8" strokeWidth="1.5" />
        <path d="M15 18L20 31L24 21L28 31L33 18" stroke="white" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
      <div className="leading-none">
        <div className="font-heading font-extrabold text-slate-900 tracking-tight text-2xl">
          WebsiteTech<sup className="text-[0.5em] font-bold ml-0.5 align-super">™</sup>
        </div>
        <div className="font-mono text-[11px] font-semibold text-brand tracking-wider mt-1">WebsiteTech.pro</div>
      </div>
    </div>
  );
}

const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null); // for mobile accordions
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAnalyze = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("analyzer"), 400);
    } else {
      scrollToId("analyzer");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav border-b border-slate-200/70 shadow-sm" : "glass-nav"}`}
      data-testid="navbar"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="nav-brand-link"><BrandLogo /></Link>

          {/* Desktop nav — 6 items + CTA — fits at 1280px laptop width */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((group, idx) => (
              <div key={group.key} className="relative group py-6">
                <button
                  data-testid={`nav-group-${group.key}`}
                  className="flex items-center gap-1 px-2 xl:px-3 py-2 text-sm font-medium text-slate-700 hover:text-brand transition-colors whitespace-nowrap rounded-md"
                >
                  {group.label} <ChevronDown size={13} className="text-slate-400 group-hover:text-brand transition-colors" />
                </button>
                {group.isMega ? (
                  <div className="fixed left-0 right-0 pt-2 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50" data-testid={`nav-mega-${group.key}`}>
                    <div className="max-w-[1200px] mx-auto bg-white border border-slate-200 rounded-xl soft-shadow p-5">
                      <div className="grid grid-cols-2 gap-3 pb-4 border-b border-slate-100">
                        {group.primary.map((p) => (
                          <Link
                            key={p.label}
                            to={p.to}
                            data-testid={`nav-mega-primary-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-brand hover:bg-blue-50 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-lg bg-blue-100 text-brand flex items-center justify-center shrink-0">
                              <p.Icon size={16} />
                            </div>
                            <div>
                              <span className="block font-semibold text-slate-900 text-sm">{p.label}</span>
                              <span className="block text-[11px] text-slate-500 mt-0.5">{p.desc}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="pt-4 grid grid-cols-5 gap-5">
                        {group.groups.map((g) => (
                          <div key={g.key} data-testid={`nav-mega-group-${g.key}`}>
                            <div className="flex items-center gap-1.5 mb-2">
                              <g.Icon size={13} className="text-brand" />
                              <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-900">{g.label}</span>
                            </div>
                            <p className="text-[10px] text-slate-400 mb-2 leading-snug">{g.tagline}</p>
                            <ul className="space-y-1">
                              {g.links.map((l) => (
                                <li key={l.label}>
                                  <Link
                                    to={l.to}
                                    data-testid={`nav-mega-link-${g.key}-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className="block text-[12px] text-slate-600 hover:text-brand transition-colors py-0.5 leading-snug"
                                  >
                                    {l.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <div className="pt-4 mt-4 border-t border-slate-100">
                        <a
                          href="/crescent"
                          target="_blank"
                          rel="noopener"
                          data-testid="nav-mega-case-crescent"
                          className="group/case flex items-center gap-4 p-3 rounded-xl bg-gradient-to-r from-emerald-50 via-white to-amber-50 border border-emerald-200 hover:border-emerald-500 transition-colors"
                        >
                          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-black text-lg shrink-0">C</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Flagship Client</span>
                              <span className="text-[10px] text-slate-500">Manufacturing · Kerala, India</span>
                            </div>
                            <div className="text-sm font-bold text-slate-900 mt-1 truncate">Crescent Flexi Pack — multilingual B2B site + AI packaging assistant</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 truncate">8 products · 10 industries · 8 languages · OpenAI TTS · grounded AI chat · exit-intent capture</div>
                          </div>
                          <span className="text-emerald-700 text-xs font-bold group-hover/case:translate-x-1 transition-transform">See it live →</span>
                        </a>
                      </div>
                      <div className="pt-4 mt-4 border-t border-slate-100 flex items-center gap-4 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                          <Compass size={11} /> Platform tools
                        </span>
                        {group.tools.map((t) => (
                          <Link
                            key={t.label}
                            to={t.to}
                            data-testid={`nav-mega-tool-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="text-xs text-slate-600 hover:text-brand font-medium"
                          >
                            {t.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`absolute top-full pt-2 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ${idx >= NAV.length - 2 ? "right-0" : "left-0"}`}>
                    <div className="bg-white border border-slate-200 rounded-xl soft-shadow min-w-[260px] p-2">
                      {group.links.map((item) => (
                        <Link
                          key={item.label}
                          to={item.to}
                          data-testid={`nav-link-${group.key}-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="block px-3 py-2 text-sm text-slate-700 hover:text-brand hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <span className="block font-medium">{item.label}</span>
                          {item.desc && <span className="block text-[11px] text-slate-400 mt-0.5">{item.desc}</span>}
                        </Link>
                      ))}
                      {group.footer && (
                        <Link
                          to={group.footer.to}
                          className="block px-3 py-2 mt-1 text-xs font-semibold text-brand hover:underline border-t border-slate-100 pt-2"
                        >
                          {group.footer.label}
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right side — utility + primary CTA */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <LanguageSwitcher variant="desktop" />
            {user ? (
              <Link to="/my-websitetech" data-testid="nav-portal-link" className="text-sm font-semibold text-slate-700 hover:text-brand whitespace-nowrap">
                My WebsiteTech
              </Link>
            ) : (
              <Link to="/login" data-testid="nav-login-link" className="hidden 2xl:inline text-sm font-medium text-slate-600 hover:text-brand transition-colors whitespace-nowrap">
                Log In
              </Link>
            )}
            <button
              onClick={goAnalyze}
              data-testid="nav-primary-cta"
              className="btn-primary rounded-lg px-3 xl:px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap shrink-0"
              aria-label={PRIMARY_CTA_LABEL}
            >
              <ClipboardCheck size={15} />
              <span className="hidden 2xl:inline">{PRIMARY_CTA_LABEL}</span>
              <span className="2xl:hidden">{PRIMARY_CTA_SHORT}</span>
            </button>
          </div>

          <button className="lg:hidden text-slate-700 p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-6 py-6 space-y-3 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="pb-1"><LanguageSwitcher variant="mobile" /></div>
          {NAV.map((group) => (
            <div key={group.key} className="border-b border-slate-100 pb-2">
              <button
                className="w-full flex items-center justify-between text-slate-800 font-semibold text-sm py-2"
                onClick={() => setOpenGroup(openGroup === group.key ? null : group.key)}
                data-testid={`nav-mobile-group-${group.key}`}
              >
                {group.label}
                <ChevronDown size={16} className={`transition-transform ${openGroup === group.key ? "rotate-180" : ""}`} />
              </button>
              {openGroup === group.key && (
                <div className="pl-3 flex flex-col gap-1 border-l border-slate-100 pb-2">
                  {group.isMega ? (
                    <>
                      {group.primary.map((p) => (
                        <Link key={p.label} to={p.to} onClick={() => setOpen(false)} className="text-sm font-semibold text-brand py-1.5">
                          {p.label}
                        </Link>
                      ))}
                      {group.groups.map((g) => (
                        <div key={g.key} className="mt-2">
                          <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 pt-1">{g.label}</div>
                          {g.links.map((l) => (
                            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="block text-sm text-slate-600 hover:text-brand py-1">
                              {l.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 pb-1">Platform tools</div>
                        {group.tools.map((t) => (
                          <Link key={t.label} to={t.to} onClick={() => setOpen(false)} className="block text-sm text-slate-600 hover:text-brand py-1">
                            {t.label}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      {group.links.map((item) => (
                        <Link key={item.label} to={item.to} onClick={() => setOpen(false)} className="text-sm text-slate-600 hover:text-brand py-1.5">
                          {item.label}
                        </Link>
                      ))}
                      {group.footer && (
                        <Link to={group.footer.to} onClick={() => setOpen(false)} className="text-xs font-semibold text-brand py-1.5">
                          {group.footer.label}
                        </Link>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          <Link to={user ? "/my-websitetech" : "/login"} onClick={() => setOpen(false)} className="block text-slate-700 font-medium pt-2">
            {user ? "My WebsiteTech" : "Log In"}
          </Link>
          <button onClick={goAnalyze} data-testid="nav-primary-cta-mobile" className="btn-primary rounded-lg w-full py-3 text-sm mt-2 flex items-center justify-center gap-2 font-semibold">
            <ClipboardCheck size={15} /> {PRIMARY_CTA_LABEL} <ArrowRight size={15} />
          </button>
          <p className="text-[11px] text-slate-400 text-center pt-2">
            Powered by <span className="font-semibold text-slate-500">SocialPulse Network</span>
          </p>
        </div>
      )}
    </header>
  );
}
